/**
 * Seat inventory.
 *
 * Seats are the unit of billing, so the page is built to make over- and
 * under-assignment impossible to miss: a reserved seat is shown as occupied because
 * it is, and the assign control only lists members who do not already hold one.
 */

import { useState } from 'react'
import { Link, Navigate } from 'react-router-dom'

import {
  useAssignSeat,
  useExport,
  useMembers,
  useOrganization,
  useRevokeSeat,
  useSeats,
} from '@/shared/api/hooks'
import { AppShell, PageHead } from '@/shared/ui/Chrome'
import {
  Badge,
  Button,
  Empty,
  ErrorNotice,
  Loading,
  Notice,
  Panel,
  Readout,
  Select,
  formatDate,
} from '@/shared/ui/primitives'
import { NoWorkspace, useCurrentOrgId } from './Team'

export function Seats() {
  const orgId = useCurrentOrgId()
  if (!orgId) return <NoWorkspace />
  return <SeatInventory orgId={orgId} />
}

function SeatInventory({ orgId }: { orgId: string }) {
  const { data: org } = useOrganization(orgId)
  const { data: seats, isPending, error } = useSeats(orgId)
  const { data: roster } = useMembers(orgId)
  const assign = useAssignSeat(orgId)
  const revoke = useRevokeSeat(orgId)
  const exportCsv = useExport(orgId)

  // Draft assignment per seat row, so two rows in flight cannot overwrite each
  // other's selection.
  const [drafts, setDrafts] = useState<Record<string, string>>({})

  const can = org?.capabilities ?? {}
  const isFreeTeam = org?.plan === 'free_team' || !org?.subscription_status
  const unseated = (roster?.members ?? []).filter(
    (member) => member.status === 'active' && !member.has_seat,
  )

  if (org && org.role === 'member') {
    return <Navigate to="/team" replace />
  }

  return (
    <AppShell>
      <PageHead
        index="06"
        eyebrow="Seats"
        title="Seat inventory"
        subtitle="A member needs an assigned seat to receive Team entitlements."
        actions={
          <>
            {can['billing.manage'] && (
              <Link to="/billing">
                <Button size="sm" variant={isFreeTeam ? 'primary' : 'default'}>
                  {isFreeTeam ? 'Upgrade to Team (3+ seats)' : 'Change seat count'}
                </Button>
              </Link>
            )}
            {(can['seats.manage'] || can['billing.manage']) && (
              <Button
                size="sm"
                variant="ghost"
                loading={exportCsv.isPending}
                onClick={() => exportCsv.mutate('seats')}
              >
                Export CSV
              </Button>
            )}
          </>
        }
      />

      <div className="stack" style={{ ['--gap' as string]: 'var(--s5)' }}>
        {org && (
          <div className="readout-grid">
            <div>
              <Readout
                value={org.seats.purchased}
                caption={isFreeTeam ? 'free seats' : 'purchased'}
              />
            </div>
            <div>
              <Readout value={org.seats.assigned} caption="assigned" tone="signal" />
            </div>
            <div>
              <Readout value={org.seats.reserved} caption="reserved" tone="amber" />
            </div>
            <div>
              <Readout
                value={org.seats.available}
                caption="available"
                tone={org.seats.available === 0 ? 'amber' : undefined}
              />
            </div>
          </div>
        )}

        {org?.seats.available === 0 && isFreeTeam ? (
          can['billing.manage'] ? (
            <Notice tone="warn">
              Both included free evaluation seats are occupied. To add more engineers to your workspace,{' '}
              <Link to="/billing" className="signal" style={{ fontWeight: 600 }}>
                upgrade to Archis Team (starts at 3 seats) →
              </Link>
            </Notice>
          ) : (
            <Notice tone="warn">
              Both included free evaluation seats are occupied. Contact your workspace Owner or Billing Admin to add more seats.
            </Notice>
          )
        ) : org?.seats.available === 0 ? (
          <Notice tone="warn">
            Every seat is occupied. Reserved seats belong to outstanding invitations —
            revoking an invitation frees its seat immediately.
          </Notice>
        ) : null}

        {unseated.length > 0 && org && org.seats.available > 0 && (
          <Notice tone="warn">
            {unseated.length} active {unseated.length === 1 ? 'member has' : 'members have'} no
            seat and cannot use Archis through this workspace.
          </Notice>
        )}

        {isPending && <Loading label="Loading seats" />}
        <ErrorNotice error={error} />
        <ErrorNotice error={assign.error} />
        <ErrorNotice error={revoke.error} />

        <Panel title="Seats" index="01" padded={false}>
          {seats && seats.length === 0 && (
            <Empty>
              No seats yet. Seats are created when a Team subscription starts.
            </Empty>
          )}

          {seats && seats.length > 0 && (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Seat</th>
                    <th>Status</th>
                    <th>Holder</th>
                    <th>Assigned</th>
                    <th className="actions" />
                  </tr>
                </thead>
                <tbody>
                  {seats.map((seat, position) => (
                    <tr key={seat.id}>
                      <td className="num">{String(position + 1).padStart(2, '0')}</td>
                      <td>
                        {seat.status === 'assigned' && (
                          <Badge tone="signal" dot>
                            assigned
                          </Badge>
                        )}
                        {seat.status === 'available' && <Badge>available</Badge>}
                        {seat.status === 'reserved_by_invitation' && (
                          <Badge tone="amber">reserved</Badge>
                        )}
                        {seat.status === 'suspended' && <Badge tone="coral">suspended</Badge>}
                      </td>
                      <td>{seat.member_email ?? <span className="muted">—</span>}</td>
                      <td className="mono-note">{formatDate(seat.assigned_at)}</td>
                      <td className="actions">
                        {can['seats.manage'] && seat.status === 'assigned' && (
                          <Button
                            size="sm"
                            variant="danger"
                            loading={revoke.isPending}
                            onClick={() => revoke.mutate(seat.id)}
                          >
                            Revoke
                          </Button>
                        )}

                        {can['seats.manage'] && seat.status === 'available' && (
                          <div
                            className="row"
                            style={{ justifyContent: 'flex-end', gap: 'var(--s2)', flexWrap: 'nowrap' }}
                          >
                            <Select
                              value={drafts[seat.id] ?? ''}
                              onChange={(event) =>
                                setDrafts((current) => ({ ...current, [seat.id]: event.target.value }))
                              }
                              options={[
                                { value: '', label: 'Assign to…' },
                                ...unseated.map((member) => ({
                                  value: member.id,
                                  label: member.email,
                                })),
                              ]}
                              style={{ maxWidth: 210 }}
                              disabled={unseated.length === 0}
                            />
                            <Button
                              size="sm"
                              disabled={!drafts[seat.id]}
                              loading={assign.isPending}
                              onClick={() =>
                                assign.mutate(
                                  { seatId: seat.id, memberId: drafts[seat.id] ?? '' },
                                  {
                                    onSuccess: () =>
                                      setDrafts((current) => {
                                        const next = { ...current }
                                        delete next[seat.id]
                                        return next
                                      }),
                                  },
                                )
                              }
                            >
                              Assign
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        <Panel title="How seats behave" index="02">
          <ul
            className="stack mono-note"
            style={{ ['--gap' as string]: 'var(--s2)', paddingLeft: 'var(--s5)', margin: 0 }}
          >
            <li>An invitation reserves a seat the moment it is sent, so it cannot be double-promised.</li>
            <li>An expired or revoked invitation releases its seat automatically.</li>
            <li>Revoking a seat blocks that member's next license refresh; their cached license lapses within 72 hours.</li>
            <li>Removing a member releases their seat and revokes their organization devices.</li>
            <li>Every assignment and revocation is recorded in the audit log.</li>
          </ul>
        </Panel>
      </div>
    </AppShell>
  )
}
